import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { auth } from '@config/firebase'; 
import { logError, logInfo } from '@utils/logger';
import { HomeFooter } from './HomeFooter'; 
import { 
  Settings,
  Bell,
  User,
  LayoutDashboard,
  Map,
  Shield,
  Truck,
  FileText,
  Clock,
  DollarSign,
  LogOut
} from 'lucide-react';

export const HomePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  console.log('Current user:', user); // Debug log

  if (loading) return null;

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      logInfo('User signed out successfully');
      navigate('/login');
    } catch (error) {
      logError('Failed to sign out:', error);
    }
  };

  const modules = [
    // Admin module - only show if user is admin
    (user?.isAdmin || user?.email === 'jfprince@soltec.ca' || user?.email === 'jfprince29@gmail.com' || user?.email === 'admin@oversizehub.net') && {
      id: 'admin',
      name: 'Admin Panel',
      description: 'System Administration',
      icon: LayoutDashboard,
      path: '/admin',
      iconColor: 'text-red-500',
      bgColor: 'bg-red-500/10'
    },
    {
      id: 'roadscope',
      name: 'RoadScope',
      description: 'Route Survey & Mapping',
      icon: Map,
      path: '/roadscope',
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      id: 'roadguard',
      name: 'RoadGuard',
      description: 'Pilot Car Management',
      icon: Shield,
      path: '/roadguard',
      iconColor: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      id: 'cargocare',
      name: 'CargoCare',
      description: 'Load & Cargo Management',
      icon: Truck,
      path: '/cargocare',
      iconColor: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      id: 'permitpro',
      name: 'PermitPro',
      description: 'Permit Management',
      icon: FileText,
      path: '/permitpro',
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10'
    },
    {
      id: 'eld',
      name: 'Electronic Logbook',
      description: 'Hours of Service & Compliance',
      icon: Clock,
      path: '/eld',
      iconColor: 'text-red-500',
      bgColor: 'bg-red-500/10'
    },
    {
      id: 'reports',
      name: 'Reports',
      description: 'Analytics & Reporting',
      icon: FileText,
      path: '/reports',
      iconColor: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10'
    },
    {
      id: 'pricing',
      name: 'Pricing',
      description: 'Plans & Billing',
      icon: DollarSign,
      path: '/plans',
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    }
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-[#2A2B2E]">
        <div className="flex items-center gap-4">
          <img 
            src={`${window.location.origin}/logo.png`} 
            alt="OverSizeHub" 
            className="h-12"
            onError={(e) => {
              console.error('Logo failed to load in home page');
              e.target.src = `${window.location.origin}/favicon.png`;
            }}
          />
          <div>
            <h1 className="text-xl font-semibold text-white">Welcome, {user?.name || user?.email}</h1>
            <p className="text-sm text-gray-400">Last login: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg bg-[#3D3F42] text-gray-400 hover:text-white"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button 
            onClick={() => navigate('/notifications')}
            className="p-2 rounded-lg bg-[#3D3F42] text-gray-400 hover:text-white"
          >
            <div className="relative">
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">3</span>
            </div>
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 rounded-lg bg-[#3D3F42] text-gray-400 hover:text-white"
          >
            <User className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Module Grid */}
      <div className="p-6 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modules.map(module => (
            <button
              key={module.id}
              onClick={() => navigate(module.path)}
              className="flex items-start gap-4 p-6 bg-[#2A2B2E] rounded-lg hover:bg-[#353639] transition-colors"
            >
              <div className={`p-3 rounded-lg ${module.bgColor}`}>
                {React.createElement(module.icon, { 
                  className: `w-8 h-8 ${module.iconColor}` 
                })}
              </div>
              <div className="text-left">
                <h2 className="text-lg font-medium text-white">{module.name}</h2>
                <p className="text-sm text-gray-400">{module.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sign Out Button */}
      <div className="fixed bottom-4 right-4 z-50">
        <button 
          onClick={handleSignOut}
          className="flex items-center gap-2 bg-gray-700/80 hover:bg-gray-600 text-gray-300 hover:text-white px-4 py-2 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
        </div>
      
      {/* Footer */}
      <HomeFooter />
    </div>
  );
};